/**
 * Generic right-click context menu.
 *
 * Portalled to <body> with position:fixed (never absolute — scroll containers clip it),
 * clamped to the viewport. Supports one level of flyout submenus, keyboard navigation
 * (arrows / Enter / Escape / ArrowLeft-Right for submenus) and closes on outside
 * pointerdown, scroll, resize, blur or another contextmenu.
 *
 * Items: { id, label, danger?, checked?, swatch?, children?: Item[] } | { divider: true } | { header: string }
 */

const MENU_CLASS = 'app-context-menu';
const VIEWPORT_MARGIN = 8;

let activeMenu = null;

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function renderItems(items) {
    return items.map((item, index) => {
        if (item.divider) return `<div class="${MENU_CLASS}__divider" role="separator"></div>`;
        if (item.header) return `<div class="${MENU_CLASS}__header">${escapeHtml(item.header)}</div>`;
        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const classes = [`${MENU_CLASS}__item`];
        if (item.danger) classes.push(`${MENU_CLASS}__item--danger`);
        if (item.checked) classes.push('is-checked');
        const swatch = item.swatch
            ? `<span class="${MENU_CLASS}__swatch" style="--swatch-color: ${escapeHtml(item.swatch)};" aria-hidden="true"></span>`
            : '';
        const check = item.checked ? `<span class="${MENU_CLASS}__check" aria-hidden="true">✓</span>` : '';
        const chevron = hasChildren ? `<span class="${MENU_CLASS}__chevron" aria-hidden="true">›</span>` : '';
        const role = item.checked !== undefined ? 'menuitemradio' : 'menuitem';
        const ariaChecked = item.checked !== undefined ? ` aria-checked="${item.checked ? 'true' : 'false'}"` : '';
        const ariaPopup = hasChildren ? ' aria-haspopup="menu" aria-expanded="false"' : '';
        return `<button type="button" class="${classes.join(' ')}" role="${role}"${ariaChecked}${ariaPopup} tabindex="-1" data-menu-index="${index}">${swatch}<span class="${MENU_CLASS}__label">${escapeHtml(item.label)}</span>${check}${chevron}</button>`;
    }).join('');
}

function createMenuElement(items, extraClass = '') {
    const el = document.createElement('div');
    el.className = extraClass ? `${MENU_CLASS} ${extraClass}` : MENU_CLASS;
    el.setAttribute('role', 'menu');
    el.innerHTML = renderItems(items);
    document.body.appendChild(el);
    return el;
}

function placeAtPoint(el, x, y) {
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const maxLeft = window.innerWidth - w - VIEWPORT_MARGIN;
    const maxTop = window.innerHeight - h - VIEWPORT_MARGIN;
    el.style.left = `${Math.max(VIEWPORT_MARGIN, Math.min(x, maxLeft))}px`;
    el.style.top = `${Math.max(VIEWPORT_MARGIN, Math.min(y, maxTop))}px`;
}

function placeBesideItem(el, itemEl) {
    const rect = itemEl.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    // Prefer opening to the right; flip left when there is no room.
    let left = rect.right + 2;
    if (left + w > window.innerWidth - VIEWPORT_MARGIN) left = rect.left - w - 2;
    let top = rect.top - 4;
    if (top + h > window.innerHeight - VIEWPORT_MARGIN) top = window.innerHeight - h - VIEWPORT_MARGIN;
    el.style.left = `${Math.max(VIEWPORT_MARGIN, left)}px`;
    el.style.top = `${Math.max(VIEWPORT_MARGIN, top)}px`;
}

function focusableButtons(el) {
    return Array.from(el.querySelectorAll(`.${MENU_CLASS}__item`));
}

export function closeContextMenu() {
    if (activeMenu) activeMenu.close();
}

/**
 * Open a context menu at viewport coordinates.
 * @param {Object} options
 * @param {number} options.x - clientX
 * @param {number} options.y - clientY
 * @param {Array} options.items - menu items (see file header)
 * @param {Function} options.onSelect - called with the selected leaf item (after the menu closes)
 * @param {Function} [options.onClose] - called once when the menu closes for any reason
 * @param {string} [options.className] - extra class for the root menu
 * @returns {Function} close
 */
export function openContextMenu({ x, y, items, onSelect, onClose, className = '' }) {
    closeContextMenu();

    const root = createMenuElement(items, className);
    placeAtPoint(root, x, y);

    let submenu = null;
    let submenuOwner = null;
    let closed = false;

    const closeSubmenu = () => {
        if (submenu) submenu.remove();
        if (submenuOwner) submenuOwner.setAttribute('aria-expanded', 'false');
        submenu = null;
        submenuOwner = null;
    };

    const openSubmenu = (ownerBtn, focusFirst) => {
        if (submenuOwner === ownerBtn) {
            if (focusFirst) focusableButtons(submenu)[0]?.focus({ preventScroll: true });
            return;
        }
        closeSubmenu();
        const parentItem = items[Number(ownerBtn.dataset.menuIndex)];
        if (!parentItem || !parentItem.children) return;
        submenu = createMenuElement(parentItem.children, `${MENU_CLASS}--submenu`);
        submenuOwner = ownerBtn;
        ownerBtn.setAttribute('aria-expanded', 'true');
        placeBesideItem(submenu, ownerBtn);
        submenu.addEventListener('click', (event) => handleClick(event, parentItem.children));
        submenu.addEventListener('keydown', (event) => handleKeydown(event, submenu, true));
        submenu.addEventListener('pointerover', (event) => {
            event.target.closest(`.${MENU_CLASS}__item`)?.focus({ preventScroll: true });
        });
        if (focusFirst) focusableButtons(submenu)[0]?.focus({ preventScroll: true });
    };

    const close = () => {
        if (closed) return;
        closed = true;
        closeSubmenu();
        root.remove();
        document.removeEventListener('pointerdown', onOutsidePointer, true);
        document.removeEventListener('contextmenu', onOutsideContext, true);
        window.removeEventListener('scroll', close, true);
        window.removeEventListener('resize', close);
        window.removeEventListener('blur', close);
        if (activeMenu && activeMenu.close === close) activeMenu = null;
        if (typeof onClose === 'function') onClose();
    };

    function handleClick(event, list) {
        const btn = event.target.closest(`.${MENU_CLASS}__item`);
        if (!btn) return;
        const item = list[Number(btn.dataset.menuIndex)];
        if (!item) return;
        if (item.children) {
            openSubmenu(btn, false);
            return;
        }
        close();
        if (typeof onSelect === 'function') onSelect(item);
    }

    function handleKeydown(event, menuEl, isSubmenu) {
        const buttons = focusableButtons(menuEl);
        const current = buttons.indexOf(document.activeElement);
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            const next = current === -1 ? (step === 1 ? 0 : buttons.length - 1) : (current + step + buttons.length) % buttons.length;
            buttons[next]?.focus();
        } else if (event.key === 'Home' || event.key === 'End') {
            event.preventDefault();
            buttons[event.key === 'Home' ? 0 : buttons.length - 1]?.focus();
        } else if (event.key === 'ArrowRight' && !isSubmenu) {
            const btn = buttons[current];
            if (btn && btn.getAttribute('aria-haspopup') === 'menu') {
                event.preventDefault();
                openSubmenu(btn, true);
            }
        } else if ((event.key === 'ArrowLeft' || event.key === 'Escape') && isSubmenu) {
            event.preventDefault();
            event.stopPropagation();
            const owner = submenuOwner;
            closeSubmenu();
            owner?.focus({ preventScroll: true });
        } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close();
        } else if (event.key === 'Tab') {
            event.preventDefault();
            close();
        }
    }

    function onOutsidePointer(event) {
        if (root.contains(event.target) || (submenu && submenu.contains(event.target))) return;
        close();
    }

    function onOutsideContext(event) {
        if (root.contains(event.target) || (submenu && submenu.contains(event.target))) {
            event.preventDefault();
            return;
        }
        close();
    }

    root.addEventListener('click', (event) => handleClick(event, items));
    root.addEventListener('keydown', (event) => handleKeydown(event, root, false));
    // Hovering a parent item opens its flyout; hovering a plain item closes any open flyout.
    root.addEventListener('pointerover', (event) => {
        const btn = event.target.closest(`.${MENU_CLASS}__item`);
        if (!btn) return;
        if (btn.getAttribute('aria-haspopup') === 'menu') openSubmenu(btn, false);
        else closeSubmenu();
        btn.focus({ preventScroll: true });
    });

    // Defer listener registration so the opening right-click doesn't immediately close it.
    window.setTimeout(() => {
        if (closed) return;
        document.addEventListener('pointerdown', onOutsidePointer, true);
        document.addEventListener('contextmenu', onOutsideContext, true);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        window.addEventListener('blur', close);
    }, 0);

    focusableButtons(root)[0]?.focus({ preventScroll: true });

    activeMenu = { close };
    return close;
}
